import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";
import classNames from "classnames";
import { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";
import EnterpriseService from "../../common/services/enterprise-service";
import { confirmWrapper } from "../ui/askConfirm";
import truckyService from "../../common/services/trucky-service";
import { useNavigate, useParams } from "react-router";
import LoadingOverlay from "react-loading-overlay";
import { EnterpriseContext } from "./EnterpriseContext";
import ProfileCardV2 from "../ProfileCardV2";
import BoxDynamicAd from "../../ads/BoxDynamicAd";

export default function EditApplication({ container }) {
  const { globalContext } = useContext(EnterpriseContext);
  const [loadingData, setLoadingData] = useState(true);
  const [application, setApplication] = useState(null);
  const [activeTab, toggleTab] = useState("details");
  const [logs, setUserLogs] = useState([]);
  const es = new EnterpriseService();
  const navigate = useNavigate();
  const [contactMessage, setContactMessage] = useState({
    message: "",
    add_cc_email: globalContext.member.email,
  });

  const { id } = useParams();

  if (!es.can(globalContext.member, "members.manage_applications")) {
    return;
  }
  
  useEffect(() => {
    truckyService.setDocumentTitle("Applications");
    getApplication();
  }, []);

  const getApplication = async () => {
    setLoadingData(true);
    const result = await es.getApplication(globalContext.company, id);
    if (result.error) {
      toast.error(result.message);
    } else {
      setApplication(result);
      getUserLogs(result.user.id);
    }
    setLoadingData(false);
  };

  const getUserLogs = async (id) => {
    const result = await es.getUserLogs(id);
    setUserLogs(
      result.filter(
        (m) =>
          m.reason == "joined" ||
          m.reason == "kicked" ||
          m.reason == "got_kicked" ||
          m.reason == "left"
      )
    );
  };

  const accept = async () => {
    if (
      await confirmWrapper(
        container.localeManager.strings.applicationConfirmation,
        { container: container }
      )
    ) {
      setLoadingData(true);
      const result = await es.acceptApplication(
        globalContext.company,
        application
      );
      if (result.success) {
        toast.success("Application accepted successfully");
        getApplication();
      } else {
        toast.error(result.message);
      }
      setLoadingData(false);
    }
  };

  const reject = async () => {
    if (application.reject_reason != null && application.reject_reason != "") {
      if (
        await confirmWrapper(
          container.localeManager.strings.applicationRejection,
          { container: container }
        )
      ) {
        setLoadingData(true);
        const result = await es.updateApplication(
          globalContext.company,
          application
        );

        if (result.success) {
          const rejectResult = await es.rejectApplication(
            globalContext.company,
            application
          );

          if (rejectResult.success) {
            toast.success("Application rejected successfully");
            getApplication();
          } else {
            toast.error(result.message);
          }
        } else {
          toast.error(result.message);
        }
        setLoadingData(false);
      }
    } else {
      toast.error(container.localeManager.strings.pleaseFillRejectReason, {
        position: "top-right",
      });
    }
  };

  const save = async () => {
    setLoadingData(true);
    const result = await es.updateApplication(
      globalContext.company,
      application
    );
    if (result.success) {
      toast.success("Application saved successfully");
      getApplication();
    } else {
      toast.error(result.message);
    }
    setLoadingData(false);
  };

  const assignToMe = async () => {
    application.editor_user_id = globalContext.member.id;
    application.status = "in_progress";

    const result = await es.updateApplication(
      globalContext.company,
      application
    );

    if (result.success) {
      toast.success("Application assigned successfully");
      getApplication();
    } else {
      toast.error(result.message);
    }
  };

  const getLogMessage = (log) => {
    if (log.reason == "joined") {
      return container.localeManager.strings.formatString(
        container.localeManager.strings.companyJoined,
        log.company == null
          ? container.localeManager.strings.deletedCompany
          : log.company.name
      );
    }

    if (
      log.reason == "got_kicked" ||
      log.reason == "kicked" ||
      log.reason == "left"
    ) {
      return container.localeManager.strings.formatString(
        container.localeManager.strings.leftCompany,
        log.company == null
          ? container.localeManager.strings.deletedCompany
          : log.company.name
      );
    }
  };

  const sendApplicationContactMessage = async () => {
    const result = await es.sendApplicationContactMessage(
      company,
      application,
      contactMessage
    );

    if (result.success) {
      toast.success(container.localeManager.strings.messageSent);
    } else {
      toast.error(result.message);
    }
  };

  return (
    <LoadingOverlay
      active={loadingData}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="" id="enterpriseContainer">
        <VTCMenu active={"/applications"} container={container}></VTCMenu>

        <Col>
          <Row noGutters>
            <Col>
              <Card className="h-100">
                <CardBody>
                  <Breadcrumbs
                    breadcrumbs={[
                      {
                        to: "/vtchub",
                        title: container.localeManager.strings.vtcHub,
                      },
                      {
                        to: "/company/members",
                        title: container.localeManager.strings.members,
                      },
                      {
                        to: "/applications",
                        title: container.localeManager.strings.applications,
                      },
                      {
                        title: container.localeManager.strings.edit,
                      },
                    ]}
                  ></Breadcrumbs>
                  <div className="d-flex justify-content-between">
                    <h5>{container.localeManager.strings.applications}</h5>
                  </div>
                  {application && (() => {
                    const statusColors = { pending: 'warning', in_progress: 'info', accepted: 'success', rejected: 'danger', retired: 'secondary' };
                    const statusColor = statusColors[application.status] || 'secondary';
                    return (
                      <>
                        {/* Page Hero */}
                        <div className="page-hero mb-4">
                          <span className="page-hero-icon">
                            <FontAwesomeIcon icon="file-signature" className="mx-0" />
                          </span>
                          <div className="flex-1">
                            <div className="d-flex align-items-center gap-3 flex-wrap">
                              <h1 className="page-hero-title mb-0">{application.user.name}</h1>
                              <Badge color={statusColor} className="fs-6 text-uppercase px-3 py-2">
                                {application.status?.replace('_', ' ')}
                              </Badge>
                            </div>
                            <div className="page-hero-subtitle">
                              Application #{application.id}
                              {application.editor != null && ` · assigned to ${application.editor.name}`}
                            </div>
                          </div>
                        </div>

                        {/* Action bar */}
                        {(application.status === 'pending' || application.status === 'in_progress') && (
                          <div className="filters-toolbar mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-2">
                              <Button size="sm" onClick={() => save()} color="primary">
                                <FontAwesomeIcon icon="save" className="me-1" />{container.localeManager.strings.save}
                              </Button>
                              {application.editor_user_id == null && (
                                <Button size="sm" onClick={() => assignToMe()} color="secondary">
                                  <FontAwesomeIcon icon="play" className="me-1" />{container.localeManager.strings.assignToMe}
                                </Button>
                              )}
                              <Button size="sm" onClick={() => navigate(`/messenger?user_id=${application.user.id}`)} color="secondary">
                                <FontAwesomeIcon icon="comment-dots" className="me-1" />{container.localeManager.strings.directMessage}
                              </Button>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Button size="sm" onClick={() => reject()} color="danger">
                                <FontAwesomeIcon icon="times" className="me-1" />{container.localeManager.strings.reject}
                              </Button>
                              <Button size="sm" onClick={() => accept()} color="success">
                                <FontAwesomeIcon icon="check" className="me-1" />{container.localeManager.strings.accept}
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {application &&
                    application.user.can_be_contacted_via_email && (
                      <Nav
                        className="nav-custom nav-line-tabs nav-line-tabs-2x fs-6 fw-bold mb-3"
                        tabs
                      >
                        <NavItem>
                          <NavLink
                            className={classNames({
                              active: activeTab === "details",
                            })}
                            onClick={() => {
                              toggleTab("details");
                            }}
                          >
                            <FontAwesomeIcon icon="file-lines" className="me-2" />
                            {container.localeManager.strings.details}
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classNames({
                              active: activeTab === "contact",
                            })}
                            onClick={() => {
                              toggleTab("contact");
                            }}
                          >
                            <FontAwesomeIcon icon="envelope" className="me-2" />
                            {container.localeManager.strings.contact}
                          </NavLink>
                        </NavItem>
                      </Nav>
                    )}
                  {application && (
                    <TabContent className="mt-3" activeTab={activeTab}>
                      <TabPane tabId="details">
                        <Row>
                          <Col sm={8}>
                            <ProfileCardV2
                              user={application.user}
                              container={container}
                              avatarSize={100}
                            />
                            {application.user.steam_profile != null && (
                              <Row className="my-3">
                                <Col sm={6}>
                                  <Button
                                    tag="a"
                                    size="sm"
                                    color="secondary"
                                    target="_blank"
                                    href={`https://steamcommunity.com/profiles/${application.user.steam_profile.steam_id}`}
                                  >
                                    <FontAwesomeIcon
                                      icon={["fab", "steam"]}
                                    ></FontAwesomeIcon>
                                    {
                                      container.localeManager.strings
                                        .viewSteamProfile
                                    }
                                  </Button>
                                </Col>
                                <Col sm={6} className="text-end">
                                  <Button
                                    tag="a"
                                    size="sm"
                                    color="secondary"
                                    target="_blank"
                                    href={`/user/${application.user.id}`}
                                  >
                                    {
                                      container.localeManager.strings
                                        .profileDetails
                                    }
                                  </Button>
                                </Col>
                              </Row>
                            )}
                            {application.discord != null && (
                              <div className="info-card p-3 mb-3 d-flex align-items-center gap-3">
                                <FontAwesomeIcon icon={["fab", "discord"]} className="text-discord mx-0 fs-4" />
                                <div>
                                  <div className="text-muted small text-uppercase fw-bold">Discord</div>
                                  <div className="fw-bold">{application.discord}</div>
                                </div>
                              </div>
                            )}

                            {application.questionnaire &&
                              Object.keys(application.questionnaire).length > 0 &&
                              Object.keys(application.questionnaire).map((key) => (
                                <div key={key} className="mb-3">
                                  <div className="fw-bold mb-1 d-flex align-items-center gap-2">
                                    <FontAwesomeIcon icon="circle-question" className="text-info mx-0" />
                                    {application.questionnaire[key].question}
                                  </div>
                                  <div className="info-card p-3" style={{ wordBreak: "break-word", whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
                                    {application.questionnaire[key].answer}
                                  </div>
                                </div>
                              ))}

                            <div className="mb-3">
                              <div className="fw-bold mb-1 d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon="comment" className="text-primary mx-0" />
                                {container.localeManager.strings.messageFromTheUser}
                              </div>
                              <div className="info-card p-3" style={{ wordBreak: "break-word", whiteSpace: "pre-wrap", overflowWrap: "break-word" }}>
                                {application.message}
                              </div>
                            </div>

                            <FormGroup>
                              <Label className="fw-bold">
                                <FontAwesomeIcon icon="times-circle" className="text-danger me-2 mx-0" />
                                {container.localeManager.strings.rejectReason}
                              </Label>
                              <textarea
                                className="form-control"
                                rows="4"
                                onChange={(e) => setApplication({ ...application, reject_reason: e.target.value })}
                              >
                                {application.reject_reason}
                              </textarea>
                            </FormGroup>

                            <FormGroup>
                              <Label className="fw-bold">
                                <FontAwesomeIcon icon="lock" className="text-warning me-2 mx-0" />
                                {container.localeManager.strings.internalComment}
                              </Label>
                              <textarea
                                className="form-control"
                                rows="4"
                                onChange={(e) => setApplication({ ...application, internal_comment: e.target.value })}
                              >
                                {application.internal_comment}
                              </textarea>
                              <FormText className="text-muted">
                                {container.localeManager.strings.internalCommentHelpText}
                              </FormText>
                            </FormGroup>

                            {application.status !== 'pending' && application.status !== 'in_progress' && (
                              <FormGroup className="mt-2">
                                <Button size="sm" onClick={() => save()} color="primary">
                                  <FontAwesomeIcon icon="save" className="me-1" />{container.localeManager.strings.save}
                                </Button>
                              </FormGroup>
                            )}
                          </Col>
                          <Col>
                            <div className="info-card p-3">
                              <div className="fw-bold mb-3 d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon="clock-rotate-left" className="text-muted mx-0" />
                                {container.localeManager.strings.activityLog}
                              </div>
                              {logs.length > 0 ? (
                                <div className="timeline-label user-activities">
                                  {logs.map((m, idx) => (
                                    <div className="timeline-item" key={idx}>
                                      <div className="timeline-label fw-bolder text-gray-800 fs-6">
                                        {container.localeManager.moment(m.created_at).format("L")}
                                      </div>
                                      <div className="timeline-badge">
                                        <FontAwesomeIcon
                                          icon="genderless"
                                          className={`${m.reason === "joined" ? "text-success" : "text-danger"} fs-1`}
                                        />
                                      </div>
                                      <div className="timeline-content d-flex">
                                        <span className="fw-bolder text-gray-800 ps-3">
                                          {getLogMessage(m)}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-muted small">{container.localeManager.strings.noActiveLogs}</div>
                              )}
                            </div>

                            <FormText className="text-muted d-block mt-3">
                              <FontAwesomeIcon icon="info-circle" className="me-1" />
                              {container.localeManager.strings.assignToMeHelpText}
                            </FormText>
                          </Col>
                        </Row>
                      </TabPane>
                      <TabPane tabId="contact">
                        <Row>
                          <Col>
                            <FormText color="muted" className="mb-3">
                              {
                                container.localeManager.strings
                                  .applicationContactHint
                              }
                            </FormText>
                            <FormGroup>
                              <Label>
                                {container.localeManager.strings.message}
                              </Label>
                              <textarea
                                className="form-control"
                                rows="5"
                                onChange={(e) =>
                                  setContactMessage({
                                    ...contactMessage,
                                    message: e.target.value,
                                  })
                                }
                              >
                                {contactMessage.message}
                              </textarea>
                            </FormGroup>
                            <FormGroup>
                              <Label>
                                {container.localeManager.strings.addCcEmail}
                              </Label>
                              <InputGroup>
                                <Input
                                  value={contactMessage.add_cc_email}
                                  type="email"
                                  onChange={(e) =>
                                    setContactMessage({
                                      ...contactMessage,
                                      add_cc_email: e.target.value,
                                    })
                                  }
                                ></Input>
                                <Button
                                  onClick={() =>
                                    setContactMessage({
                                      ...contactMessage,
                                      add_cc_email: "",
                                    })
                                  }
                                  color="danger"
                                >
                                  {container.localeManager.strings.noThanks}
                                </Button>
                              </InputGroup>
                              <FormText color="muted">
                                {container.localeManager.strings.addCcEmailHint}
                              </FormText>
                            </FormGroup>
                            {contactMessage.message.trim() != "" && (
                              <FormGroup>
                                <Button
                                  onClick={sendApplicationContactMessage}
                                  color="primary"
                                >
                                  {
                                    container.localeManager.strings
                                      .sendMessageToUser
                                  }
                                </Button>
                              </FormGroup>
                            )}
                          </Col>
                        </Row>
                      </TabPane>
                    </TabContent>
                  )}
                </CardBody>
              </Card>
            </Col>
            <aside className="ad-column-placeholder">
              {!window.overwolf && (
                <BoxDynamicAd container={container} className="my-3" />
              )}
            </aside>
          </Row>
        </Col>
      </Row>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </LoadingOverlay>
  );
}
